// Admin management — Super Admin only.
import React, { useEffect, useState } from 'react';
import { adminAxios, useAdminAuth } from './AdminAuthContext';
import { Loader2, Plus, ShieldCheck, ShieldOff, AlertCircle, X, KeyRound, Copy, Check, MessageCircle } from 'lucide-react';

const SUPPORT_WHATSAPP = '5581981739909';

const fmtDate = (iso) => {
  if (!iso) return 'Nunca acessou';
  try { return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};

const RoleBadge = ({ role }) => (
  <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${role === 'super_admin' ? 'bg-[#1E3A5F] text-white' : 'bg-[#F4F6F8] text-[#475569]'}`}>
    {role === 'super_admin' ? 'Super Admin' : 'Visualizador'}
  </span>
);

const InviteModal = ({ open, onClose, onCreated }) => {
  const { token } = useAdminAuth();
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [senha, setSenha] = useState('');
  const [emailRecuperacao, setEmailRecuperacao] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) { setNome(''); setEmail(''); setRole('viewer'); setSenha(''); setEmailRecuperacao(''); setErr(''); }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (senha.length < 8) { setErr('Senha inicial deve ter ao menos 8 caracteres.'); return; }
    if (!emailRecuperacao.includes('@')) { setErr('E-mail de recuperação obrigatório e válido.'); return; }
    if (emailRecuperacao.trim().toLowerCase() === email.trim().toLowerCase()) {
      setErr('O e-mail de recuperação deve ser diferente do e-mail de acesso.');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await adminAxios(token).post('/api/admin/admins', {
        nome: nome.trim(),
        email: email.trim().toLowerCase(),
        role,
        senha_inicial: senha,
        email_recuperacao: emailRecuperacao.trim().toLowerCase(),
      });
      onCreated?.(resp.data);
      onClose();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Falha ao criar admin');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-black text-[#0F1C2E]">Convidar novo admin</h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#475569]"><X className="w-4 h-4" /></button>
        </div>
        {err && (
          <div className="flex items-start gap-2 p-2 mb-3 rounded bg-[#FEE2E2] border border-[#FECACA] text-[#7F1D1D] text-xs">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {err}
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Nome" value={nome} onChange={e => setNome(e.target.value)}
                 className="w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="invite-nome" />
          <input required type="email" placeholder="E-mail" value={email} onChange={e => setEmail(e.target.value)}
                 className="w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="invite-email" />
          <select value={role} onChange={e => setRole(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="invite-role">
            <option value="viewer">Visualizador</option>
            <option value="super_admin">Super Admin</option>
          </select>
          <input required type="password" placeholder="Senha inicial (mínimo 8 caracteres)"
                 value={senha} onChange={e => setSenha(e.target.value)}
                 className="w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="invite-senha" />
          <input required type="email" placeholder="E-mail de recuperação (obrigatório)"
                 value={emailRecuperacao} onChange={e => setEmailRecuperacao(e.target.value)}
                 className="w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm" data-testid="invite-recovery-email" />
          <p className="text-[10px] text-[#94A3B8]">O admin receberá nesse endereço um link para confirmar (válido por 24h). É também o canal de recuperação de senha.</p>
          <button type="submit" disabled={submitting}
                  className="w-full bg-[#1E3A5F] text-white font-bold py-2 rounded text-sm disabled:opacity-60 flex items-center justify-center"
                  data-testid="invite-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Criar admin'}
          </button>
        </form>
      </div>
    </div>
  );
};

const ResetPasswordModal = ({ open, target, onClose }) => {
  const { token } = useAdminAuth();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { senha_temporaria, email, nome }
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) { setResult(null); setErr(''); setSubmitting(false); setCopied(false); }
  }, [open, target]);

  if (!open || !target) return null;

  const confirm = async () => {
    setErr(''); setSubmitting(true);
    try {
      const resp = await adminAxios(token).post(`/api/admin/admins/${target.id}/reset-password`);
      setResult(resp.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Falha ao resetar senha');
    } finally { setSubmitting(false); }
  };

  const copy = async () => {
    if (!result?.senha_temporaria) return;
    try {
      await navigator.clipboard.writeText(result.senha_temporaria);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* ignore */ }
  };

  const whatsappLink = result
    ? `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(
        `Olá ${result.nome || ''}, sua nova senha temporária do painel Tô de Olho é: ${result.senha_temporaria}\n` +
        `Acesse https://app.todeolho.inf.br/admin/login e troque a senha no primeiro acesso.`
      )}`
    : '#';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="admin-reset-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-black text-[#0F1C2E] flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#1E3A5F]" /> Resetar senha
          </h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#475569]"><X className="w-4 h-4" /></button>
        </div>

        {!result && (
          <>
            <p className="text-sm text-[#0F1C2E] mb-2">
              Gerar nova senha temporária para <strong>{target.nome}</strong>?
            </p>
            <p className="text-xs text-[#475569] mb-4">
              {target.email}<br />
              A senha atual será invalidada imediatamente. O admin terá que trocar
              a senha no próximo login.
            </p>
            {err && (
              <div className="flex items-start gap-2 p-2 mb-3 rounded bg-[#FEE2E2] border border-[#FECACA] text-[#7F1D1D] text-xs">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {err}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={onClose}
                      className="flex-1 px-3 py-2 border border-[#E2E8F0] rounded text-sm text-[#475569] hover:bg-[#F8FAFC]"
                      data-testid="admin-reset-cancel">Cancelar</button>
              <button onClick={confirm} disabled={submitting}
                      className="flex-1 bg-[#1E3A5F] hover:bg-[#16304b] text-white font-bold px-3 py-2 rounded text-sm disabled:opacity-60 flex items-center justify-center"
                      data-testid="admin-reset-confirm">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Gerar senha temporária'}
              </button>
            </div>
          </>
        )}

        {result && (
          <>
            <div className="rounded border border-[#FCD34D] bg-[#FFFBEB] p-3 mb-3">
              <p className="text-[11px] uppercase font-bold text-[#92400E] mb-1">Senha temporária — copie agora</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-base font-black text-[#0F1C2E] bg-white border border-[#FDE68A] rounded px-3 py-2 select-all"
                      data-testid="admin-reset-temp-password">{result.senha_temporaria}</code>
                <button onClick={copy}
                        className="px-2.5 py-2 bg-[#1E3A5F] hover:bg-[#16304b] text-white rounded text-xs font-bold inline-flex items-center gap-1"
                        data-testid="admin-reset-copy">
                  {copied ? <><Check className="w-3.5 h-3.5" /> Copiado</> : <><Copy className="w-3.5 h-3.5" /> Copiar</>}
                </button>
              </div>
              <p className="text-[10px] text-[#92400E] mt-2">
                Esta senha não será exibida novamente. Repasse pelo canal seguro (WhatsApp/ligação).
                O admin será obrigado a trocá-la no próximo login.
              </p>
            </div>
            <div className="text-xs text-[#475569] mb-3">
              <div><strong>Para:</strong> {result.nome}</div>
              <div><strong>E-mail:</strong> {result.email}</div>
            </div>
            <div className="flex flex-col gap-2">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-3 py-2 rounded text-sm"
                 data-testid="admin-reset-whatsapp">
                <MessageCircle className="w-4 h-4" /> Enviar pelo WhatsApp
              </a>
              <button onClick={onClose}
                      className="px-3 py-2 border border-[#E2E8F0] rounded text-sm text-[#475569] hover:bg-[#F8FAFC]"
                      data-testid="admin-reset-close">Fechar</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const AdminAdmins = () => {
  const { token, admin: me, isSuper } = useAdminAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const resp = await adminAxios(token).get('/api/admin/admins');
      setAdmins(resp.data.admins || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [token]);

  const toggleActive = async (a) => {
    if (!isSuper) return;
    if (!window.confirm(a.ativo ? `Desativar ${a.email}? Ele(a) perderá acesso imediatamente.` : `Reativar ${a.email}?`)) return;
    await adminAxios(token).patch(`/api/admin/admins/${a.id}`, { ativo: !a.ativo });
    reload();
  };

  const changeRole = async (a, role) => {
    if (!isSuper || a.role === role) return;
    if (!window.confirm(`Alterar nível de ${a.email} para ${role === 'super_admin' ? 'Super Admin' : 'Visualizador'}?`)) return;
    await adminAxios(token).patch(`/api/admin/admins/${a.id}`, { role });
    reload();
  };

  return (
    <div className="space-y-4" data-testid="admin-admins-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-black text-[#0F1C2E]">Gestão de admins</h1>
          <p className="text-xs text-[#475569]">Total: {admins.length}</p>
        </div>
        {isSuper && (
          <button onClick={() => setShowInvite(true)}
                  className="inline-flex items-center gap-1.5 bg-[#1E3A5F] hover:bg-[#16304b] text-white px-3 py-2 rounded text-xs font-bold"
                  data-testid="admin-invite-btn">
            <Plus className="w-3.5 h-3.5" /> Convidar admin
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1E3A5F]" /></div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#475569] border-b border-[#E2E8F0]">
                <th className="px-3 py-2 text-left">Nome</th>
                <th className="px-3 py-2 text-left">E-mail</th>
                <th className="px-3 py-2 text-left">Nível</th>
                <th className="px-3 py-2 text-left">Último acesso</th>
                <th className="px-3 py-2 text-left">Status</th>
                {isSuper && <th className="px-3 py-2 text-right">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id} className="border-b border-[#F4F6F8] hover:bg-[#F8FAFC]">
                  <td className="px-3 py-2 font-medium text-[#0F1C2E]">{a.nome}{a.id === me?.id && <span className="text-[10px] text-[#94A3B8] ml-1">(você)</span>}</td>
                  <td className="px-3 py-2 text-[#475569] text-xs">{a.email}</td>
                  <td className="px-3 py-2"><RoleBadge role={a.role} /></td>
                  <td className="px-3 py-2 text-[#475569] text-xs">{fmtDate(a.ultimo_acesso)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${a.ativo ? 'bg-[#DCFCE7] text-[#006030]' : 'bg-[#FEE2E2] text-[#7F1D1D]'}`}>
                      {a.ativo ? 'Ativo' : 'Desativado'}
                    </span>
                  </td>
                  {isSuper && (
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {a.role !== 'super_admin' && a.id !== me?.id && (
                          <button
                            onClick={() => changeRole(a, 'super_admin')}
                            title="Promover a Super Admin"
                            className="p-1.5 rounded hover:bg-[#F4F6F8] text-[#1E3A5F]"
                            data-testid={`admin-promote-${a.id}`}
                          ><ShieldCheck className="w-3.5 h-3.5" /></button>
                        )}
                        {a.role === 'super_admin' && a.id !== me?.id && (
                          <button
                            onClick={() => changeRole(a, 'viewer')}
                            title="Rebaixar a Visualizador"
                            className="p-1.5 rounded hover:bg-[#F4F6F8] text-[#475569]"
                          ><ShieldOff className="w-3.5 h-3.5" /></button>
                        )}
                        {a.id !== me?.id && (
                          <button
                            onClick={() => setResetTarget(a)}
                            title="Resetar senha (gera senha temporária)"
                            className="p-1.5 rounded hover:bg-[#FEF3C7] text-[#92400E]"
                            data-testid={`admin-reset-pw-${a.id}`}
                          ><KeyRound className="w-3.5 h-3.5" /></button>
                        )}
                        {a.id !== me?.id && (
                          <button
                            onClick={() => toggleActive(a)}
                            className={`px-2 py-1 rounded text-[10px] font-bold ${a.ativo ? 'bg-[#FEE2E2] text-[#7F1D1D] hover:bg-[#FECACA]' : 'bg-[#DCFCE7] text-[#006030] hover:bg-[#BBF7D0]'}`}
                            data-testid={`admin-toggle-${a.id}`}
                          >{a.ativo ? 'Desativar' : 'Reativar'}</button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InviteModal open={showInvite} onClose={() => setShowInvite(false)} onCreated={() => reload()} />
      <ResetPasswordModal open={!!resetTarget} target={resetTarget} onClose={() => setResetTarget(null)} />
    </div>
  );
};

export default AdminAdmins;
